import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Utensils } from 'lucide-react';

const Catering = () => {
  const [activeTab, setActiveTab] = useState('cuisine');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const menuByCuisine = {
    american: {
      appetizers: [
        'Buffalo Wings with Blue Cheese',
        'Loaded Potato Skins',
        'Mozzarella Sticks',
        'Shrimp Cocktail',
        'Spinach Artichoke Dip'
      ],
      entrees: [
        'Grilled Ribeye Steak',
        'BBQ Ribs',
        'Roasted Turkey with Gravy',
        'Honey Glazed Ham',
        'Beef Brisket'
      ],
      sides: [
        'Mac and Cheese',
        'Coleslaw',
        'Baked Beans',
        'Corn on the Cob',
        'Caesar Salad'
      ],
      desserts: [
        'Apple Pie',
        'Chocolate Brownies',
        'Cheesecake',
        'Pecan Pie'
      ]
    },
    southern: {
      appetizers: [
        'Fried Green Tomatoes',
        'Pimento Cheese & Crackers',
        'Deviled Eggs',
        'Hush Puppies',
        'Fried Okra'
      ],
      entrees: [
        'Fried Chicken',
        'Pulled Pork BBQ',
        'Catfish Fillet',
        'Country Fried Steak',
        'Smothered Pork Chops'
      ],
      sides: [
        'Collard Greens',
        'Black-Eyed Peas',
        'Cornbread',
        'Sweet Potato Casserole',
        'Mashed Potatoes & Gravy'
      ],
      desserts: [
        'Peach Cobbler',
        'Banana Pudding',
        'Red Velvet Cake',
        'Sweet Potato Pie'
      ]
    },
    caribbean: {
      appetizers: [
        'Jerk Chicken Skewers',
        'Coconut Shrimp',
        'Plantain Chips with Mango Salsa',
        'Saltfish Fritters',
        'Conch Fritters'
      ],
      entrees: [
        'Jerk Chicken',
        'Curry Goat',
        'Oxtail Stew',
        'Grilled Red Snapper',
        'Stewed Chicken'
      ],
      sides: [
        'Rice and Peas',
        'Fried Plantains',
        'Festival (Sweet Fried Dough)',
        'Callaloo',
        'Coleslaw'
      ],
      desserts: [
        'Rum Cake',
        'Coconut Drops',
        'Gizzada (Coconut Tart)',
        'Pineapple Upside Down Cake'
      ]
    }
  };

  const menuByEventType = {
    corporate: [
      'Continental Breakfast Platters',
      'Sandwich & Wrap Platters',
      'Hot Lunch Buffets',
      'Coffee & Refreshment Stations',
      'Dessert Bars'
    ],
    wedding: [
      'Elegant Passed Hors d\'oeuvres',
      'Carving Stations',
      'Multi-Course Plated Dinners',
      'Champagne & Cocktail Service',
      'Custom Wedding Cakes'
    ],
    party: [
      'Build-Your-Own Taco Bar',
      'BBQ Buffet',
      'Seafood Boil',
      'Finger Foods & Appetizers',
      'Dessert Station'
    ]
  };

  const glutenFreeMenu = {
    appetizers: [
      'Caprese Skewers (Fresh Mozzarella, Tomato, Basil)',
      'Shrimp Cocktail with Cocktail Sauce',
      'Stuffed Mushrooms',
      'Vegetable Crudités with Hummus',
      'Deviled Eggs',
      'Bacon-Wrapped Dates',
      'Grilled Chicken Skewers'
    ],
    entreesAmerican: [
      'Grilled Ribeye Steak with Chimichurri',
      'Herb Roasted Chicken',
      'Baked Salmon with Lemon Butter',
      'BBQ Ribs (with GF sauce)',
      'Roasted Turkey Breast'
    ],
    entreesSouthern: [
      'Blackened Catfish',
      'Grilled Chicken with Collard Greens',
      'BBQ Pulled Pork (with GF sauce)',
      'Southern Style Pot Roast',
      'Cajun Shrimp and Grits (with GF grits)'
    ],
    entreesCaribbean: [
      'Jerk Chicken (with GF jerk marinade)',
      'Curry Goat',
      'Grilled Red Snapper with Mango Salsa',
      'Oxtail Stew',
      'Caribbean Grilled Shrimp'
    ],
    sides: [
      'Roasted Sweet Potatoes',
      'Steamed Vegetables',
      'Rice and Peas',
      'Mashed Potatoes',
      'Grilled Asparagus',
      'Garden Salad with GF Dressing',
      'Roasted Brussels Sprouts',
      'Quinoa Pilaf',
      'Fried Plantains'
    ],
    desserts: [
      'Flourless Chocolate Cake',
      'Fresh Fruit Platter',
      'Coconut Macaroons',
      'Crème Brûlée',
      'GF Brownies',
      'Rum Cake (GF version)',
      'Panna Cotta with Berry Compote'
    ]
  };

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" 
        data-testid="catering-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(255, 229, 217, 0.9), rgba(255, 204, 188, 0.9)), url(https://images.unsplash.com/photo-1555244162-803834f70033?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-[#8b4513] drop-shadow-lg" data-testid="catering-title">Catering Services</h1>
          <div className="decorative-line bg-[#8b4513]"></div>
          <p className="hero-subtitle text-[#6b4423] drop-shadow-md" data-testid="catering-subtitle">
            Exquisite cuisine from three culinary traditions
          </p>
        </div>
      </section>

      {/* Menu Section */}
      <section className="section" style={{ background: '#ffe5d9' }} data-testid="catering-menu-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="section-title">Our Menus</h2>
          <div className="decorative-line"></div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-8">
            <TabsList className="grid h-auto w-full max-w-3xl mx-auto grid-cols-3 items-stretch gap-1 bg-[#fff5e6]" data-testid="catering-tabs">
              <TabsTrigger value="cuisine" className="min-w-0 min-h-12 whitespace-normal px-2 leading-5 sm:min-h-0 sm:px-3" data-testid="tab-cuisine">By Cuisine</TabsTrigger>
              <TabsTrigger value="event" className="min-w-0 min-h-12 whitespace-normal px-2 leading-5 sm:min-h-0 sm:px-3" data-testid="tab-event">By Event Type</TabsTrigger>
              <TabsTrigger value="glutenfree" className="min-w-0 min-h-12 whitespace-normal px-2 leading-5 sm:min-h-0 sm:px-3" data-testid="tab-glutenfree">Gluten-Free Menu</TabsTrigger>
            </TabsList>

            {/* By Cuisine Tab */}
            <TabsContent value="cuisine" className="mt-8" data-testid="tab-content-cuisine">
              <div className="space-y-8">
                {/* American Cuisine */}
                <div className="card" data-testid="cuisine-american">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513] flex items-center">
                    <Utensils className="mr-2" /> American Cuisine
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Appetizers</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.american.appetizers.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Entrees</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.american.entrees.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Sides</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.american.sides.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Desserts</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.american.desserts.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Southern Cuisine */}
                <div className="card" data-testid="cuisine-southern">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513] flex items-center">
                    <Utensils className="mr-2" /> Southern Cuisine
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Appetizers</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.southern.appetizers.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Entrees</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.southern.entrees.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Sides</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.southern.sides.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Desserts</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.southern.desserts.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Caribbean Cuisine */}
                <div className="card" data-testid="cuisine-caribbean">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513] flex items-center">
                    <Utensils className="mr-2" /> Caribbean Cuisine
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Appetizers</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.caribbean.appetizers.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Entrees</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.caribbean.entrees.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Sides</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.caribbean.sides.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#d2691e] mb-2">Desserts</h4>
                      <ul className="space-y-1 text-[#6b4423]">
                        {menuByCuisine.caribbean.desserts.map((item, idx) => (
                          <li key={idx}>• {item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* By Event Type Tab */}
            <TabsContent value="event" className="mt-8" data-testid="tab-content-event">
              <div className="space-y-6">
                <div className="card" data-testid="event-corporate">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513]">Corporate Events</h3>
                  <ul className="space-y-2 text-[#6b4423]">
                    {menuByEventType.corporate.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card" data-testid="event-wedding">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513]">Weddings</h3>
                  <ul className="space-y-2 text-[#6b4423]">
                    {menuByEventType.wedding.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="card" data-testid="event-party">
                  <h3 className="text-2xl font-bold mb-4 text-[#8b4513]">Parties & Celebrations</h3>
                  <ul className="space-y-2 text-[#6b4423]">
                    {menuByEventType.party.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </TabsContent>

            {/* Gluten-Free Menu Tab */}
            <TabsContent value="glutenfree" className="mt-8" data-testid="tab-content-glutenfree">
              <div className="card bg-gradient-to-br from-[#fff5e6] to-[#ffecd2]">
                <div className="text-center mb-8">
                  <h3 className="text-3xl font-bold text-[#8b4513] mb-3">Complete Gluten-Free Menu</h3>
                  <p className="text-[#6b4423] max-w-3xl mx-auto">
                    All dishes are prepared with dedicated gluten-free equipment and ingredients to prevent cross-contamination.
                    Our gluten-free menu offers the same delicious flavors across all our cuisines.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                  {/* Appetizers */}
                  <div>
                    <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                      <span className="mr-2">🍤</span> Appetizers
                    </h4>
                    <ul className="space-y-2 text-[#6b4423]">
                      {glutenFreeMenu.appetizers.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Entrees by Cuisine */}
                  <div>
                    <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                      <span className="mr-2">🍖</span> Entrees
                    </h4>
                    
                    <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">American</h5>
                    <ul className="space-y-1 text-[#6b4423] mb-4 text-sm">
                      {glutenFreeMenu.entreesAmerican.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">Southern</h5>
                    <ul className="space-y-1 text-[#6b4423] mb-4 text-sm">
                      {glutenFreeMenu.entreesSouthern.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>

                    <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">Caribbean</h5>
                    <ul className="space-y-1 text-[#6b4423] text-sm">
                      {glutenFreeMenu.entreesCaribbean.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Sides */}
                  <div>
                    <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                      <span className="mr-2">🥗</span> Sides
                    </h4>
                    <ul className="space-y-2 text-[#6b4423]">
                      {glutenFreeMenu.sides.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Desserts */}
                  <div>
                    <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                      <span className="mr-2">🍰</span> Desserts
                    </h4>
                    <ul className="space-y-2 text-[#6b4423]">
                      {glutenFreeMenu.desserts.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-white/70 rounded-lg border-2 border-[#d2691e]">
                  <p className="text-[#8b4513] text-center font-semibold text-lg">
                    <span className="text-2xl mr-2">✓</span>
                    All gluten-free dishes are prepared in a dedicated gluten-free area with separate equipment and utensils to ensure safety for those with celiac disease or gluten sensitivity.
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Farm to Table Section */}
          <div className="mt-16">
            <div className="card bg-gradient-to-br from-[#a8e6cf] to-[#dcedc1] max-w-4xl mx-auto" data-testid="farm-to-table-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="text-5xl">🌾</div>
                <h3 className="text-2xl font-bold text-[#8b4513]">Farm to Table</h3>
              </div>
              <p className="text-[#6b4423] mb-4 leading-relaxed">
                We partner with local farms and suppliers to bring you the freshest, most flavorful ingredients. 
                Our farm-to-table approach ensures peak freshness, supports local agriculture, and delivers superior 
                taste in every dish.
              </p>
              <div className="space-y-2">
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">✓</span>
                  <span className="text-[#6b4423]">Locally sourced seasonal produce</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">✓</span>
                  <span className="text-[#6b4423]">Fresh, never frozen meats and seafood</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">✓</span>
                  <span className="text-[#6b4423]">Support for local farmers and sustainability</span>
                </div>
                <div className="flex items-start">
                  <span className="text-[#d2691e] mr-2">✓</span>
                  <span className="text-[#6b4423]">Organic options available</span>
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Gluten-Free Menu Section */}
          <div className="mt-16">
            <h2 className="section-title text-center">Complete Gluten-Free Menu</h2>
            <div className="decorative-line"></div>
            <p className="text-center text-[#6b4423] mt-4 mb-8 max-w-3xl mx-auto">
              All dishes are prepared with dedicated gluten-free equipment and ingredients to prevent cross-contamination.
              Our gluten-free menu offers the same delicious flavors across all our cuisines.
            </p>

            <div className="card bg-gradient-to-br from-[#fff5e6] to-[#ffecd2]" data-testid="gluten-free-menu-detailed">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {/* Appetizers */}
                <div>
                  <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                    <span className="mr-2">🍤</span> Appetizers
                  </h4>
                  <ul className="space-y-2 text-[#6b4423]">
                    {glutenFreeMenu.appetizers.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Entrees by Cuisine */}
                <div>
                  <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                    <span className="mr-2">🍖</span> Entrees
                  </h4>
                  
                  <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">American</h5>
                  <ul className="space-y-1 text-[#6b4423] mb-4 text-sm">
                    {glutenFreeMenu.entreesAmerican.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">Southern</h5>
                  <ul className="space-y-1 text-[#6b4423] mb-4 text-sm">
                    {glutenFreeMenu.entreesSouthern.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <h5 className="font-semibold text-[#8b4513] mb-2 text-sm">Caribbean</h5>
                  <ul className="space-y-1 text-[#6b4423] text-sm">
                    {glutenFreeMenu.entreesCaribbean.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Sides */}
                <div>
                  <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                    <span className="mr-2">🥗</span> Sides
                  </h4>
                  <ul className="space-y-2 text-[#6b4423]">
                    {glutenFreeMenu.sides.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Desserts */}
                <div>
                  <h4 className="font-bold text-[#d2691e] mb-4 text-xl flex items-center">
                    <span className="mr-2">🍰</span> Desserts
                  </h4>
                  <ul className="space-y-2 text-[#6b4423]">
                    {glutenFreeMenu.desserts.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#d2691e] mr-2 mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-4 bg-white/50 rounded-lg border-2 border-[#d2691e]">
                <p className="text-[#8b4513] text-center font-semibold">
                  <span className="text-2xl mr-2">✓</span>
                  All gluten-free dishes are prepared in a dedicated gluten-free area with separate equipment and utensils to ensure safety for those with celiac disease or gluten sensitivity.
                </p>
              </div>
            </div>
          </div>

          {/* Additional Dietary Info */}
          <div className="card mt-8 bg-[#fff5e6]" data-testid="dietary-info-card">
            <h3 className="text-2xl font-bold text-[#8b4513] mb-4 text-center">We Accommodate All Dietary Needs</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-full h-32 mb-3 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1623428187969-5da2dcea5ebf?w=400&q=80" 
                    alt="Vegan Food"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-bold text-[#8b4513]">Vegan</p>
              </div>
              <div className="text-center">
                <div className="w-full h-32 mb-3 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1723588636244-e82f63cb01e3?w=400&q=80" 
                    alt="Vegetarian Food"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-bold text-[#8b4513]">Vegetarian</p>
              </div>
              <div className="text-center">
                <div className="w-full h-32 mb-3 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1623428323429-cf7a1f3872fa?w=400&q=80" 
                    alt="Dairy-Free Food"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-bold text-[#8b4513]">Dairy-Free</p>
              </div>
              <div className="text-center">
                <div className="w-full h-32 mb-3 rounded-lg overflow-hidden shadow-md">
                  <img 
                    src="https://images.unsplash.com/photo-1540914124281-342587941389?w=400&q=80" 
                    alt="Gluten-Free Food"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="font-bold text-[#8b4513]">Gluten-Free</p>
              </div>
            </div>
            <p className="text-center text-[#6b4423] mt-4">
              <strong>Note:</strong> Please inform us of any allergies or dietary restrictions when booking. 
              We take food allergies seriously and will work with you to ensure a safe, delicious meal.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <p className="text-lg text-[#6b4423] mb-6">
              All menus can be customized to meet your specific needs and dietary requirements.
            </p>
            <Link to="/contact">
              <button className="btn-primary" data-testid="catering-quote-btn">
                Request a Quote
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Catering;